import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  VenueTastingEvent,
  EventGuest,
  EventStatusResponse,
  EventMode,
  TastingStep,
} from '@/types';

interface EventState {
  // Current event data
  event: VenueTastingEvent | null;
  guest: EventGuest | null;
  status: EventStatusResponse | null;

  // WebSocket state
  isEventStarted: boolean;
  isStepLocked: boolean;
  lockedStep: TastingStep | null;
  moderatorMessage: string | null;
  isWebSocketConnected: boolean;

  // UI state
  isLoading: boolean;
  error: string | null;

  // Actions
  setEvent: (event: VenueTastingEvent | null) => void;
  setGuest: (guest: EventGuest | null) => void;
  setStatus: (status: EventStatusResponse | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;

  // WebSocket actions
  setEventStarted: (started: boolean) => void;
  setStepLocked: (locked: boolean, step: TastingStep | null) => void;
  setModeratorMessage: (message: string | null) => void;
  setWebSocketConnected: (connected: boolean) => void;

  // Computed helpers
  isEventActive: () => boolean;
  isGuestJoined: () => boolean;
  canProceed: (step: TastingStep) => boolean;
  getEventMode: () => EventMode;
}

const initialState = {
  event: null,
  guest: null,
  status: null,
  isEventStarted: false,
  isStepLocked: false,
  lockedStep: null as TastingStep | null,
  moderatorMessage: null as string | null,
  isWebSocketConnected: false,
  isLoading: false,
  error: null,
};

export const useEventStore = create<EventState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setEvent: (event) => set({ event }),
      setGuest: (guest) => set({ guest }),
      setStatus: (status) => set({ status }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),

      // WebSocket actions
      setEventStarted: (isEventStarted) => set({ isEventStarted }),
      setStepLocked: (isStepLocked, lockedStep) => set({ isStepLocked, lockedStep }),
      setModeratorMessage: (moderatorMessage) => set({ moderatorMessage }),
      setWebSocketConnected: (isWebSocketConnected) => set({ isWebSocketConnected }),

      reset: () => set(initialState),

      isEventActive: () => {
        const { event } = get();
        return event?.status === 'active';
      },

      isGuestJoined: () => {
        const { guest } = get();
        return guest?.status === 'joined' || guest?.status === 'completed';
      },

      canProceed: (step: TastingStep) => {
        const { event, status, isStepLocked, lockedStep } = get();

        // In async mode, always allow proceeding
        if (event?.tasting_mode === 'async') {
          return true;
        }

        // In sync/hybrid mode, check if step is locked (via WebSocket or status)
        const locked = isStepLocked || status?.is_locked;
        const currentLockedStep = lockedStep || status?.current_step;

        if (locked && currentLockedStep) {
          // Can only proceed to the current locked step or earlier
          const stepOrder: TastingStep[] = ['intro', 'aroma', 'taste', 'finish', 'botanicals', 'summary'];
          const currentIndex = stepOrder.indexOf(currentLockedStep);
          const targetIndex = stepOrder.indexOf(step);
          return targetIndex <= currentIndex;
        }

        return true;
      },

      getEventMode: () => {
        const { event } = get();
        return event?.tasting_mode || 'async';
      },
    }),
    {
      name: 'tasting-event-storage',
      partialize: (state) => ({
        guest: state.guest,
        event: state.event ? { uuid: state.event.uuid, access_code: state.event.access_code } : null,
      }),
    }
  )
);

export default useEventStore;
